import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router';
import * as microsoftTeams from '@microsoft/teams-js';

import { useDisconnectExistingTeam } from '../../hooks/useConnectExistingTeam';
import { linkageFailureIsDefinitive } from './teamsConnectionMessages';
import { provideRemoveWorker } from './teamsRemoveBridge';

/**
 * Rendered in the Teams removal frame when a user removes the PRATUS tab, via
 * the `removeUrl` that TeamsTabConfig stores on the tab. Removing the tab is
 * the only signal Teams gives us that the Team no longer wants PRATUS, so it is
 * what disconnects the incident.
 */
const TeamsTabRemove: React.FC = () => {
  const [theme, setTheme] = useState<string>('dark');
  const [searchParams] = useSearchParams();
  // Absent when the tab predates removeUrl, or when the tab was added outside a Team; there is nothing to disconnect.
  const [teamGroupId, setTeamGroupId] = useState<string | undefined>(undefined);
  const [contextReady, setContextReady] = useState(false);

  const incidentId = searchParams.get('incidentId') ?? undefined;
  const { mutateAsync: disconnectExistingTeam } = useDisconnectExistingTeam();

  useEffect(() => {
    const initialize = async () => {
      try {
        await microsoftTeams.app.initialize();

        const context = await microsoftTeams.app.getContext();

        setTeamGroupId(context.team?.groupId);

        // Teams withholds the remove event until the frame reports it loaded; without this the handler never fires.
        microsoftTeams.app.notifySuccess();

        if (context.app.theme === 'dark' || context.app.theme === 'contrast') {
          setTheme('dark');
          document.querySelectorAll('body').forEach((item) => {
            item.style.backgroundColor = 'transparent';
            item.style.color = 'white';
          });
        } else {
          setTheme('light');
          document.querySelectorAll('body').forEach((item) => {
            item.style.backgroundColor = 'transparent';
            item.style.color = 'black';
          });
        }
      } catch (e) {
        console.warn('Teams Tab Removal Initialization Error: ', e);
      } finally {
        // Publish the worker even on failure, so the registered handler is never left waiting for one.
        setContextReady(true);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    if (!contextReady) return;

    provideRemoveWorker(async () => {
      if (!incidentId || !teamGroupId) {
        console.warn(
          'PRATUS: no incident id or Team group id in the removal context, so nothing was disconnected.'
        );
        return { disconnected: true };
      }

      try {
        await disconnectExistingTeam({ incidentId, teamsId: teamGroupId });
        return { disconnected: true };
      } catch (error) {
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;

        if (linkageFailureIsDefinitive(status)) {
          const message =
            (axios.isAxiosError(error) && error.response?.data?.error) ||
            'Could not disconnect this incident workspace from the Team site.';
          return { disconnected: false, message };
        }

        // Inconclusive failure. Saying so would only alarm the user about a linkage they can clear from PRATUS.
        console.error(
          'PRATUS: could not disconnect the incident from this Team site. The tab was still removed.',
          {
            status,
            data: axios.isAxiosError(error) && error.response?.data,
            error,
          }
        );
        return { disconnected: true };
      }
    });
  }, [contextReady, incidentId, teamGroupId, disconnectExistingTeam]);

  return (
    <div style={{ padding: 24, maxWidth: 600 }}>
      <div style={{ color: theme === 'dark' ? 'white' : 'black' }}>
        <strong>Remove PRATUS from this Team</strong>
        <br />
        <br />
        Removing this tab disconnects this Team site from its PRATUS incident.
        Roster changes, file uploads and notifications will stop flowing between
        them.
        <br />
        <br />
        <strong>Nothing is deleted.</strong> The incident, this Team, its
        channels, its SharePoint files and its members all stay exactly as they
        are. You can reconnect at any time by adding the PRATUS tab again.
      </div>
    </div>
  );
};

export default TeamsTabRemove;
